/**
 * Wire protocol shared between the Bun WebSocket server and the browser client.
 *
 * Design: the server is authoritative for lobby membership, join codes and the
 * race lifecycle (lobby -> countdown -> racing -> finished) and relays each
 * player's self-reported transform to everyone else at TICK_HZ. Each client
 * simulates its own kart (arcade physics) and reports position/lap progress.
 * Finish order is authoritative on the server by arrival order of `finished`.
 */

/** Snapshot broadcast rate (server -> clients) and client report rate. */
export const TICK_HZ = 20;
/** Laps required to finish a race. */
export const TOTAL_LAPS = 3;
/** Countdown shown before a race starts, in milliseconds. */
export const COUNTDOWN_MS = 3500;
/** Max players per lobby (grid size). */
export const MAX_PLAYERS = 8;
/** After the first finisher, auto-end the race after this long. */
export const RACE_END_GRACE_MS = 45_000;
/** Default track id (must match a track in the client's TRACKS registry). */
export const DEFAULT_TRACK_ID = 'sunset-loop';
/** Max entries returned for a leaderboard query. */
export const LEADERBOARD_SIZE = 10;
/**
 * Fastest lap the server will believe. The shortest circuit is ~1040 units and
 * the quickest vehicle tops out at 86.8 u/s even on permanent boost, so 12 s is
 * the theoretical floor; 8 s leaves headroom for future tracks while still
 * rejecting a client that claims to have finished instantly.
 */
export const MIN_LAP_MS = 8_000;
/** How long a dropped player keeps their seat (and race position) to return to. */
export const RECONNECT_GRACE_MS = 90_000;
/** Bots the host may add to fill out a grid. */
export const MAX_BOTS = 7;

/**
 * How hard the bots try. The host picks one for the lobby and simulates the
 * whole field at that standard; the value spreads a little across the grid so
 * the pack still strings out rather than running nose-to-tail.
 */
export const BOT_SKILLS = ['rookie', 'club', 'pro', 'ace'] as const;
export type BotSkill = (typeof BOT_SKILLS)[number];
export const DEFAULT_BOT_SKILL: BotSkill = 'club';

/**
 * Centre of the skill band each setting races at, 0 (clumsy) .. 1 (quick).
 *
 * Calibrated on the Sunset Loop, three laps, in the rubber duck: roughly 88 s
 * for a Rookie, 73 s for a Club driver, 62 s for a Pro and 58 s for an Ace. The
 * top of the raw scale is deliberately left unused — past about 0.92 a bot
 * over-commits and starts losing time again.
 */
export const BOT_SKILL_VALUES: Record<BotSkill, number> = {
  rookie: 0.25,
  club: 0.5,
  pro: 0.72,
  ace: 0.92,
};

export function isBotSkill(value: unknown): value is BotSkill {
  return typeof value === 'string' && (BOT_SKILLS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------
/**
 * Playing is anonymous by default; an account only buys you a place on the
 * leaderboard. An account name is therefore a public identity — it labels a kart
 * in the lobby and a row on the board — so it is held to what a lobby row can
 * show (16 chars) and to characters that read the same everywhere.
 */
export const ACCOUNT_NAME_MIN = 3;
export const ACCOUNT_NAME_MAX = 16;
export const PASSWORD_MIN = 6;
/** Cap the input so a huge string cannot be handed to the password hasher. */
export const PASSWORD_MAX = 128;
/** How long a signed-in browser stays signed in. */
export const SESSION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const ACCOUNT_NAME_CHARS = /^[A-Za-z0-9_-]+$/;

/**
 * Validate an account name, returning a message to show or null when it is
 * fine. Shared so the client can refuse the obvious cases without a round trip
 * and still say exactly what the server would have said.
 */
export function accountNameError(name: unknown): string | null {
  if (typeof name !== 'string') return 'Enter a name.';
  const trimmed = name.trim();
  if (trimmed.length < ACCOUNT_NAME_MIN) {
    return `Name needs at least ${ACCOUNT_NAME_MIN} characters.`;
  }
  if (trimmed.length > ACCOUNT_NAME_MAX) {
    return `Name can be at most ${ACCOUNT_NAME_MAX} characters.`;
  }
  if (!ACCOUNT_NAME_CHARS.test(trimmed)) {
    return 'Name can use letters, numbers, - and _ only.';
  }
  return null;
}

/** Validate a password the same way on both sides. Null means acceptable. */
export function passwordError(password: unknown): string | null {
  if (typeof password !== 'string') return 'Enter a password.';
  if (password.length < PASSWORD_MIN) {
    return `Password needs at least ${PASSWORD_MIN} characters.`;
  }
  if (password.length > PASSWORD_MAX) {
    return `Password can be at most ${PASSWORD_MAX} characters.`;
  }
  return null;
}

export type LobbyState = 'lobby' | 'countdown' | 'racing' | 'finished';

export interface PlayerInfo {
  id: string;
  name: string;
  vehicleId: string;
  ready: boolean;
  isHost: boolean;
  connected: boolean;
  /** Simulated by the host rather than driven by a person. */
  isBot: boolean;
  /**
   * Racing under a signed-in account, so this name is theirs and their time
   * counts. Guests race under whatever they typed and are not recorded.
   */
  authed: boolean;
  finished: boolean;
  /** ms from race start to finish, or null if not finished. */
  finishTime: number | null;
  /** final placing (1 = winner), or null while racing. */
  place: number | null;
}

export interface LobbyInfo {
  code: string;
  state: LobbyState;
  hostId: string;
  trackId: string;
  totalLaps: number;
  /** How hard the bots try. Host-chosen; only the host acts on it. */
  botSkill: BotSkill;
  players: PlayerInfo[];
}

/** One player's kinematic state inside a snapshot. Arrays keep messages small. */
export interface PlayerSnapshot {
  id: string;
  /** position x,y,z */
  p: [number, number, number];
  /** orientation quaternion x,y,z,w */
  q: [number, number, number, number];
  /** velocity x,y,z (used for client-side extrapolation) */
  v: [number, number, number];
  /** current lap (0-based until first finish line crossing) */
  lap: number;
  /** index of the last checkpoint passed */
  cp: number;
  /** finished the race */
  fin: boolean;
}

/**
 * Everything a returning client needs to drop straight back into a race in
 * progress. The server has been relaying this player's transform all along, so
 * a reconnect resumes where they were rather than restarting them on the grid.
 */
export interface RaceResume {
  /** Milliseconds already elapsed in the running race. */
  elapsedMs: number;
  p: [number, number, number];
  q: [number, number, number, number];
  v: [number, number, number];
  lap: number;
  cp: number;
}

/** One bot's kinematic state, reported by the host that simulates it. */
export interface BotState {
  id: string;
  p: [number, number, number];
  q: [number, number, number, number];
  v: [number, number, number];
  lap: number;
  cp: number;
}

/** One global-leaderboard record (best race time for a player on a track). */
export interface LeaderboardEntry {
  trackId: string;
  name: string;
  timeMs: number;
  /** epoch ms when the time was recorded */
  at: number;
}

// ---------------------------------------------------------------------------
// Client -> Server
// ---------------------------------------------------------------------------
/**
 * `token` identifies a player across reconnects. The client generates one and
 * keeps it in localStorage, so a refresh mid-race reclaims the same seat. It
 * doubles as the player id inside a room. Note this means a client picks its own
 * id: fine among friends, but it is not an authentication mechanism.
 */
export type ClientMessage =
  | { type: 'createLobby'; name: string; vehicleId: string; token: string }
  | { type: 'joinLobby'; code: string; name: string; vehicleId: string; token: string }
  | { type: 'leaveLobby' }
  | { type: 'selectVehicle'; vehicleId: string }
  | { type: 'setReady'; ready: boolean }
  | { type: 'startRace' }
  | {
      type: 'state';
      p: [number, number, number];
      q: [number, number, number, number];
      v: [number, number, number];
      lap: number;
      cp: number;
    }
  /** Race duration is timed by the server, so this carries no time of its own. */
  | { type: 'finished' }
  | { type: 'rematch' }
  | { type: 'selectTrack'; trackId: string }
  | { type: 'getLeaderboard'; trackId: string | null }
  /** Host only: resize the bot field. */
  | { type: 'setBots'; count: number }
  /** Host only: set how hard the bots try. */
  | { type: 'setBotSkill'; skill: BotSkill }
  /** Host only: relay the bots it simulates, at TICK_HZ. */
  | { type: 'botStates'; bots: BotState[] }
  /** Host only: a bot crossed the line. */
  | { type: 'botFinished'; id: string }
  /** Create an account and sign in with it. */
  | { type: 'register'; name: string; password: string }
  | { type: 'login'; name: string; password: string }
  /**
   * Re-establish a previous sign-in on a fresh socket, from the session token
   * the client kept. Sent on connect and after every reconnect, since the
   * account rides on the connection.
   */
  | { type: 'resumeSession'; session: string }
  | { type: 'logout' };

// ---------------------------------------------------------------------------
// Server -> Client
// ---------------------------------------------------------------------------
export type ServerMessage =
  /** `resume` is present only when rejoining a race already under way. */
  | { type: 'joined'; playerId: string; lobby: LobbyInfo; resume?: RaceResume }
  | { type: 'lobbyUpdate'; lobby: LobbyInfo }
  | { type: 'error'; message: string }
  /** Race is about to start; show a `ms` countdown, then wait for `raceStart`. */
  | { type: 'countdown'; ms: number }
  /** Authoritative GO — unlock controls and start the lap timer now. */
  | { type: 'raceStart' }
  | { type: 'snapshot'; players: PlayerSnapshot[] }
  | { type: 'playerFinished'; id: string; place: number; time: number }
  | { type: 'raceFinished'; lobby: LobbyInfo }
  | { type: 'playerLeft'; id: string }
  | { type: 'leaderboard'; trackId: string | null; entries: LeaderboardEntry[] }
  /**
   * The authoritative answer to "who am I signed in as", sent after every
   * register / login / logout / resumeSession. `account` null means guest, and
   * `session` is the token to keep (or null to forget the stored one).
   */
  | { type: 'auth'; account: string | null; session: string | null }
  /** A sign-in attempt was refused; `message` is safe to show as-is. */
  | { type: 'authError'; message: string };

export type ServerMessageType = ServerMessage['type'];

/** Narrow a decoded server message by its `type`. */
export type ServerMessageOf<T extends ServerMessageType> = Extract<ServerMessage, { type: T }>;

export function encode(msg: ServerMessage | ClientMessage): string {
  return JSON.stringify(msg);
}

export function decode<T = ServerMessage | ClientMessage>(raw: string): T {
  return JSON.parse(raw) as T;
}
